import React, { useRef, type RefObject } from 'react';
import styled, { css } from 'styled-components';

import useClickOutside from '~/hooks/useClickOutside';
import PortalledPositioner from '../PortalledPositioner';
import type { RootId } from '../Portal';

export type Props = {
  isVisible: boolean;
  onClickOutside?: (e: MouseEvent) => void;
  openerRef: RefObject<HTMLElement>;
  rootId: RootId;
};

const ResponsivePopup: React.FCC<Props> = ({
  dataTestId,
  onClickOutside,
  isVisible,
  openerRef,
  rootId,
  children,
}) => {
  const childrenRef = useRef<HTMLDivElement>(null);

  useClickOutside({
    callback: isVisible && onClickOutside ? onClickOutside : () => {},
    allowClickInPortals: rootId ? [rootId] : undefined,
    allowClickInRefs: [childrenRef, { current: openerRef.current }],
  });

  return (
    <PortalledPositioner
      isOpen={isVisible}
      rootId={rootId}
      openerRef={openerRef}
      dataTestId={dataTestId}
    >
      <div ref={childrenRef}>{children}</div>
    </PortalledPositioner>
  );
};

// Wrap the children with this component if you want to style the element
export const BoxShadowContainer = styled.div<{ $width?: string }>`
  ${({ theme, $width }) => css`
    width: ${$width || '200px'};
    border-radius: ${theme.getTokens().border.radius.base};
    background-color: ${theme.color('white')};
    padding: ${theme.space('base')};
    box-shadow: ${theme.boxShadow('base')};
  `}
`;

export default ResponsivePopup;
